/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, ReactEventHandler} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import Indicator, {IndicatorProps} from '../components/indicators/Indicator';
import {dlsTypography, dlsZIndex} from '../theme/theme';
import {Color, DLSTheme} from '../types/types';

// defines the prop definitions for the session timeout ...
export interface UserNotificationDialogProps extends IndicatorProps {
  icon?: ReactElement;
  capitalize?: boolean;
  show: boolean;
  title: string;
  message: string | JSX.Element;
  actionLabel?: string | JSX.Element;
  dismissLabel?: string;
  zIndex?: number;
  maxWidth?: number;
  iconMargin?: string;
  allowWhitespace?: boolean;
  curvedCorners?: boolean;
  onAction?: () => void;
  onDismiss?: () => void;
  onBackdropClick?: ReactEventHandler;
};

// defines the styles used by this component ...
const useStyles = makeStyles<DLSTheme, UserNotificationDialogProps>(({dls}) => ({
  icon : {
    display       : 'inline-block',
    verticalAlign : 'top',
    margin        : ({iconMargin}) => iconMargin || undefined,
    paddingTop    : ({indicator}) => indicator ? -3 : 2,
    marginRight   : 6
  },
  paper : {
    borderRadius : ({curvedCorners}) => curvedCorners ? '5px' : 0,
    maxWidth     : ({maxWidth}) => maxWidth ? `${maxWidth}px` : undefined,
    margin       : '25px !important'
  },
  userNotificationRoot : {
    zIndex : ({zIndex}) => `${zIndex ?? dlsZIndex('userNotificationDialogRoot')} !important` as any
  },
  title : {
    ...dlsTypography('h4'),
    textTransform : ({capitalize}) => capitalize ? 'uppercase' : 'none',
    padding       : '20px 20px 0px 24px',
    lineHeight    : '24px',
    letterSpacing : 1
  },
  content : {
    whiteSpace : ({allowWhitespace}) => allowWhitespace ? 'pre-line' : undefined,
    padding    : '0 20px !important',
    margin     : '10px 0 15px'
  },
  actions : {
    justifyContent : 'flex-start',
    padding        : '0px 18px 8px !important',
    marginTop      : -6
  }
}));

/**
 * The UserNotification dialog serves to provide the generic dialog notification
 * which is to be used for prompting a user before making an important change,
 * such as deleting an item, changing their password, etc..
 *
 * @see https://www.figma.com/file/z9yUzIaskZRhgpYIhLyh91/03_Hero-Screens?node-id=632%3A82003
 *
 *
 * ```typescript
 *   import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
 *
 *   export const SessionTimeout = ({show, onLogin}: SessionTimeoutProps) => {
 *     return (
 *       <UserNotificationDialog show={show}
 *         icon={<NotificationDisconnectIcon />}
 *         title={translate('notifications:session.title')}
 *         message={translate('notifications:session.message')}
 *         actionLabel={translate('notifications:session.login')}
 *         onAction={onLogin} />
 *     );
 *   };
 *
 * ```
 *
 */
export const UserNotificationDialog = (props: UserNotificationDialogProps) => {
  const {
    icon,
    show,
    title,
    message,
    actionLabel,
    dismissLabel,
    indicator,
    onAction,
    onDismiss,
    onBackdropClick
  } = props;
  const classes = useStyles(props);
  if (show) {
    const hasActions = Boolean(onAction || onDismiss);
    return (
      <Dialog open classes={{
        root  : classes.userNotificationRoot,
        paper : classes.paper
      }}
      onBackdropClick={onBackdropClick}>
        <DialogTitle className={classes.title}>
          {icon && <div className={classes.icon}>{icon}</div>}
          {indicator ? (<div className={classes.icon}><Indicator variant={indicator}/></div>) : undefined}
          {title}
        </DialogTitle>
        <DialogContent className={classes.content}>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        {hasActions &&
          <DialogActions className={classes.actions}>
            {onAction &&
              <TertiaryButton style={{padding: 0}} onClick={onAction}>
                {actionLabel}
              </TertiaryButton>}
            {onDismiss &&
              <TertiaryButton onClick={onDismiss} isAlt altColor={Color.grey3}>
                {dismissLabel}
              </TertiaryButton>}
          </DialogActions>}
      </Dialog>
    );
  }
  return null;
};
export default UserNotificationDialog;
